// frontend/src/components/communityBasedAssessment/CommunityBasedAssessmentPage.jsx
import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { getApiBaseURL } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import { toast } from "react-toastify";
import CommunityBasedAssessmentForm from "./CommunityBasedAssessmentForm";
import CommunityBasedAssessmentView from "./CommunityBasedAssessmentView";

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const CommunityBasedAssessmentPage = () => {
  const { user, tenant_id: tenantId } = useContext(AuthContext);
  const apiBase = getApiBaseURL();
  const userId = user?.user_id;
  const userRole = user?.role;
  const isAdmin = user?.is_admin;

  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list"); // 'list', 'form', 'view'
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Force layout recalculation when view changes
  useEffect(() => {
    window.scrollTo(0, 0);
    window.dispatchEvent(new Event('resize'));
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 100);
    return () => clearTimeout(timer);
  }, [view]);

  // Load assessments
  const loadAssessments = async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const res = await secureAxios.get(
        `${apiBase}/api/community-based-assessments`,
        {
          params: { 
            tenant_id: tenantId, 
            user_id: userId,
            role: userRole 
          },
        }
      );
      setAssessments(res.data);
    } catch (err) {
      console.error("Error loading assessments:", err);
      toast.error("Failed to load assessments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAssessments();
  }, [tenantId, userId]);

  // Filter assessments
  const filteredAssessments = assessments.filter((a) => {
    const matchesSearch =
      !searchTerm ||
      a.consumer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.completed_by_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.assessment_site_name?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || a.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Handle delete
  const handleDelete = async (assessmentId) => {
    if (!window.confirm("Are you sure you want to delete this assessment?")) {
      return;
    }

    try {
      await secureAxios.delete(
        `${apiBase}/api/community-based-assessments/${assessmentId}`,
        { params: { tenant_id: tenantId } }
      );
      toast.success("Assessment deleted successfully");
      loadAssessments();
    } catch (err) {
      console.error("Error deleting assessment:", err);
      toast.error("Failed to delete assessment");
    }
  };

  // Handle generate PDF
  const handleGeneratePDF = async (assessmentId) => {
    try {
      toast.info("Generating PDF...");
      const res = await secureAxios.get(
        `${apiBase}/api/community-based-assessments/pdf/${assessmentId}`,
        { params: { tenant_id: tenantId } }
      );

      if (res.data.pdf_url) {
        window.open(res.data.pdf_url, "_blank");
        toast.success("PDF generated successfully");
      }
    } catch (err) {
      console.error("Error generating PDF:", err);
      toast.error("Failed to generate PDF");
    }
  };

  // Render form view
  if (view === "form") {
    return (
      <div className="community-based-assessment-page">
        <CommunityBasedAssessmentForm
          assessment={selectedAssessment}
          onCancel={() => {
            setView("list");
            setSelectedAssessment(null);
          }}
          onSave={() => {
            setView("list");
            setSelectedAssessment(null);
            loadAssessments();
          }}
          onGeneratePDF={selectedAssessment ? () => handleGeneratePDF(selectedAssessment.assessment_id) : null}
        />
      </div>
    );
  }

  // Render detail view
  if (view === "view") {
    return (
      <div className="community-based-assessment-page">
        <CommunityBasedAssessmentView
          assessmentId={selectedAssessment?.assessment_id}
          onBack={() => {
            setView("list");
            setSelectedAssessment(null);
          }}
          onEdit={() => {
            setView("form");
          }}
          onGeneratePDF={() => handleGeneratePDF(selectedAssessment?.assessment_id)}
        />
      </div>
    );
  }

  // Render list view
  return (
    <div className="community-based-assessment-page">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h4 className="mb-1">
            <i className="bi bi-clipboard-data me-2"></i>
            <span className="d-none d-sm-inline">CRP-21: Community Based Assessment</span>
            <span className="d-sm-none">CRP-21</span>
          </h4>
          <p className="text-muted mb-0 d-none d-sm-block">
            Manage community based assessment worksheets for consumers
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => {
            setSelectedAssessment(null);
            setView("form");
          }}
        >
          <i className="bi bi-plus-circle me-1"></i>
          <span className="d-none d-sm-inline">New Assessment</span>
          <span className="d-sm-none">New</span>
        </button>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body py-3">
          <div className="row g-2 g-md-3">
            <div className="col-12 col-md-6">
              <div className="input-group">
                <span className="input-group-text">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="col-8 col-md-3">
              <select
                className="form-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="completed">Completed</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            <div className="col-4 col-md-3 text-end">
              <span className="badge bg-secondary">
                {filteredAssessments.length}
                <span className="d-none d-sm-inline"> Assessment{filteredAssessments.length !== 1 ? "s" : ""}</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Assessment List */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : filteredAssessments.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-5">
            <i className="bi bi-clipboard-data display-4 text-muted mb-3 d-block"></i>
            <h5>No Assessments Found</h5>
            <p className="text-muted">
              {searchTerm || statusFilter !== "all"
                ? "Try adjusting your search filters"
                : "Click 'New Assessment' to create your first CRP-21"}
            </p>
            {!searchTerm && statusFilter === "all" && (
              <button
                className="btn btn-primary mt-2"
                onClick={() => {
                  setSelectedAssessment(null);
                  setView("form");
                }}
              >
                <i className="bi bi-plus-circle me-1"></i>
                Create Assessment
              </button>
            )}
          </div>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="card d-none d-md-block">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Consumer</th>
                    <th>Completed By</th>
                    <th>Assessment Site</th>
                    <th>Created</th>
                    <th>Status</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAssessments.map((assessment) => (
                    <tr key={assessment.assessment_id}>
                      <td>
                        <strong>{assessment.consumer_name}</strong>
                      </td>
                      <td>{assessment.completed_by_name || "-"}</td>
                      <td>
                        {assessment.assessment_site_name ||
                          assessment.assessment_site_text ||
                          "-"}
                      </td>
                      <td>{formatDate(assessment.created_at)}</td>
                      <td>
                        <span
                          className={`badge ${
                            assessment.status === "completed"
                              ? "bg-success"
                              : assessment.status === "archived"
                              ? "bg-secondary"
                              : "bg-warning text-dark"
                          }`}
                        >
                          {assessment.status === "completed"
                            ? "Completed"
                            : assessment.status === "archived"
                            ? "Archived"
                            : "Draft"}
                        </span>
                      </td>
                      <td className="text-end">
                        <div className="btn-group btn-group-sm">
                          <button
                            className="btn btn-outline-primary"
                            title="View"
                            onClick={() => {
                              setSelectedAssessment(assessment);
                              setView("view");
                            }}
                          >
                            <i className="bi bi-eye"></i>
                          </button>
                          <button
                            className="btn btn-outline-secondary"
                            title="Edit"
                            onClick={() => {
                              setSelectedAssessment(assessment);
                              setView("form");
                            }}
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button
                            className="btn btn-outline-info"
                            title="Generate PDF"
                            onClick={() =>
                              handleGeneratePDF(assessment.assessment_id)
                            }
                          >
                            <i className="bi bi-file-pdf"></i>
                          </button>
                          <button
                            className="btn btn-outline-danger"
                            title="Delete"
                            onClick={() =>
                              handleDelete(assessment.assessment_id)
                            }
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Card View */}
          <div className="d-md-none">
            {filteredAssessments.map((assessment) => (
              <div className="card mb-3" key={assessment.assessment_id}>
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <h6 className="card-title mb-0">
                      <i className="bi bi-person me-1"></i>
                      {assessment.consumer_name}
                    </h6>
                    <span
                      className={`badge ${
                        assessment.status === "completed"
                          ? "bg-success"
                          : assessment.status === "archived"
                          ? "bg-secondary"
                          : "bg-warning text-dark"
                      }`}
                    >
                      {assessment.status === "completed"
                        ? "Completed"
                        : assessment.status === "archived"
                        ? "Archived"
                        : "Draft"}
                    </span>
                  </div>
                  
                  <div className="small text-muted mb-2">
                    <div className="mb-1">
                      <i className="bi bi-person-badge me-1"></i>
                      <strong>By:</strong> {assessment.completed_by_name || "-"}
                    </div>
                    <div className="mb-1">
                      <i className="bi bi-geo-alt me-1"></i>
                      <strong>Site:</strong> {assessment.assessment_site_name || assessment.assessment_site_text || "-"}
                    </div>
                    <div>
                      <i className="bi bi-calendar me-1"></i>
                      <strong>Created:</strong> {formatDate(assessment.created_at)}
                    </div>
                  </div>

                  <div className="d-flex gap-2 mt-3">
                    <button
                      className="btn btn-outline-primary btn-sm flex-fill"
                      onClick={() => {
                        setSelectedAssessment(assessment);
                        setView("view");
                      }}
                    >
                      <i className="bi bi-eye me-1"></i>
                      View
                    </button>
                    <button
                      className="btn btn-outline-secondary btn-sm flex-fill"
                      onClick={() => {
                        setSelectedAssessment(assessment);
                        setView("form");
                      }}
                    >
                      <i className="bi bi-pencil me-1"></i>
                      Edit
                    </button>
                    <button
                      className="btn btn-outline-info btn-sm"
                      title="Generate PDF"
                      onClick={() => handleGeneratePDF(assessment.assessment_id)}
                    >
                      <i className="bi bi-file-pdf"></i>
                    </button>
                    <button
                      className="btn btn-outline-danger btn-sm"
                      title="Delete"
                      onClick={() => handleDelete(assessment.assessment_id)}
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default CommunityBasedAssessmentPage;